import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Plus,
  Settings,
  ChevronLeft,
  ChevronRight,
  ThumbsUp,
  Palette,
  ExternalLink,
  Users,
  TrendingUp,
  Check,
  ChevronDown,
  Loader2,
  Facebook,
  Instagram,
  Linkedin,
  X,
  Image as ImageIcon,
  RefreshCw,
  Sparkles,
  Calendar,
  Trash2,
  HelpCircle,
  Zap,
  MessageCircle,
  Send,
  User,
  AlertCircle,
  Twitter,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { useWorkspaceStore } from '../store/workspace';
import { AI_EMPLOYEES } from '../data/employees';
import BrandKitModal from '../components/BrandKitModal';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Helper to strip markdown syntax for clean text previews
const stripMarkdown = (text: string): string => {
  return text
    .replace(/#{1,6}\s*/g, '') // Remove headings
    .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold
    .replace(/\*([^*]+)\*/g, '$1') // Remove italic
    .replace(/__([^_]+)__/g, '$1') // Remove bold (underscore)
    .replace(/_([^_]+)_/g, '$1') // Remove italic (underscore)
    .replace(/~~([^~]+)~~/g, '$1') // Remove strikethrough
    .replace(/`([^`]+)`/g, '$1') // Remove inline code
    .replace(/^\s*[-*+]\s+/gm, '') // Remove list markers
    .replace(/^\s*\d+\.\s+/gm, '') // Remove numbered list markers
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links, keep text
    .trim();
};

interface Integration {
  id: number;
  provider: string;
  provider_name: string;
  provider_username?: string;
  provider_email?: string;
  status: string;
  scopes?: string;
}

interface ScheduledPost {
  id: number;
  target_platforms: string[];
  content: string;
  scheduled_at: string;
  status: 'scheduled' | 'posting' | 'posted' | 'failed' | 'draft' | 'cancelled';
  image_url?: string;
  content_type?: string;
  created_at: string;
  posted_at?: string;
  facebook_page_id?: string;
  post_results?: { facebook?: { post_id: string; success: boolean } }; // Parsed object from backend
}

interface FacebookPage {
  id: string;
  name: string;
  access_token?: string;
}

interface InstagramAccount {
  id: string;
  username: string;
  name?: string;
}

interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface ContentTheme {
  id: string;
  name: string;
  frequency: string;
  platforms: string[];
  enabled: boolean;
}

interface FacebookComment {
  id: string;
  message: string;
  author_name: string;
  author_id: string;
  created_at: string;
  reply_count: number;
}

interface CommentReply {
  id: string;
  message: string;
  author_name: string;
  author_id: string;
  created_at: string;
}

// Facebook post fetched directly from Facebook API
interface FacebookApiPost {
  id: string; // Facebook post ID (format: pageId_postId)
  message: string;
  created_time: string;
  full_picture?: string;
  permalink_url?: string;
  shares_count: number;
  reactions_count: number;
  comments_count: number;
}

// Default content themes (based on Sintra.ai)
const defaultContentThemes: ContentTheme[] = [
  {
    id: 'ai-speed-simplicity',
    name: 'AI-Powered Speed & Simplicity Demo',
    frequency: '1-2 posts per week',
    platforms: ['linkedin', 'linkedin-organization'],
    enabled: true,
  },
  {
    id: 'behind-scenes',
    name: 'Behind-the-Scenes & Company Culture',
    frequency: '1 post per month',
    platforms: ['linkedin', 'linkedin-organization'],
    enabled: false,
  },
  {
    id: 'branding-customization',
    name: 'Branding & Customization Showcase',
    frequency: '1 post every 2 weeks',
    platforms: ['linkedin', 'linkedin-organization'],
    enabled: false,
  },
  {
    id: 'success-stories',
    name: 'Client Success Stories & Testimonials',
    frequency: '1 post every 10 days',
    platforms: ['linkedin', 'linkedin-organization'],
    enabled: true,
  },
  {
    id: 'compliance-security',
    name: 'Compliance & Security Assurance',
    frequency: '1 post per week',
    platforms: ['linkedin', 'linkedin-organization'],
    enabled: true,
  },
  {
    id: 'payment-integration',
    name: 'Payment Integration & Convenience',
    frequency: '1 post per month',
    platforms: ['linkedin', 'linkedin-organization'],
    enabled: false,
  },
];

// Social media providers for onboarding
const socialMediaProviders = [
  {
    id: 'facebook',
    name: 'Facebook',
    icon: Facebook,
    color: 'bg-blue-600',
    gradient: 'from-blue-600 to-blue-700',
    hasPages: true,
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: Instagram,
    color: 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400',
    gradient: 'from-purple-600 via-pink-500 to-orange-400',
    hasAccounts: true,
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: Linkedin,
    color: 'bg-blue-700',
    gradient: 'from-blue-700 to-blue-800',
    hasOrganization: true,
  },
  {
    id: 'twitter',
    name: 'Twitter / X',
    icon: Twitter,
    color: 'bg-black',
    gradient: 'from-gray-900 to-black',
  },
];

// Social media employees
const socialMediaEmployees = AI_EMPLOYEES.filter(
  emp => emp.category === 'marketing' || emp.id === 'aria'
);

// Parse scheduled_at string as local time (not UTC)
// The backend returns times in "YYYY-MM-DD HH:MM:SS" format (user's local time)
// JavaScript's Date constructor interprets this as UTC, so we need to parse manually
function parseScheduledAtAsLocal(scheduledAt: string): Date {
  try {
    // Handle edge cases
    if (!scheduledAt || typeof scheduledAt !== 'string') {
      console.error('[parseScheduledAtAsLocal] Invalid input:', scheduledAt);
      return new Date(); // Return current date as fallback
    }

    // Handle both formats: "YYYY-MM-DD HH:MM:SS" and ISO format with T
    const cleanStr = scheduledAt.replace('T', ' ').replace('Z', '').split('.')[0];
    const parts = cleanStr.split(' ');

    if (parts.length < 1) {
      console.error('[parseScheduledAtAsLocal] Cannot parse:', scheduledAt);
      return new Date();
    }

    const datePart = parts[0];
    const timePart = parts[1] || '00:00:00';

    const dateParts = datePart.split('-');
    if (dateParts.length !== 3) {
      console.error('[parseScheduledAtAsLocal] Invalid date format:', datePart);
      return new Date();
    }

    const [year, month, day] = dateParts.map(Number);
    const [hours, minutes, seconds] = timePart.split(':').map(Number);

    // Validate parsed values
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      console.error('[parseScheduledAtAsLocal] Invalid date values:', { year, month, day });
      return new Date();
    }

    // Create date using local timezone by passing individual components
    return new Date(year, month - 1, day, hours || 0, minutes || 0, seconds || 0);
  } catch (error) {
    console.error('[parseScheduledAtAsLocal] Error parsing:', scheduledAt, error);
    return new Date();
  }
}

export default function SocialMediaManager() {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const { theme } = useThemeStore();
  const { currentWorkspace } = useWorkspaceStore();
  const isDark = theme === 'dark';

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [scheduledPosts, setScheduledPosts] = useState<ScheduledPost[]>([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    return new Date(today.setDate(diff));
  });

  // Create Content Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Selected post for sidebar
  const [selectedPost, setSelectedPost] = useState<ScheduledPost | null>(null);

  // Sidebar editing state
  const [editedContent, setEditedContent] = useState('');
  const [editedPlatform, setEditedPlatform] = useState('');
  const [editedDate, setEditedDate] = useState('');
  const [editedTime, setEditedTime] = useState('');
  const [isRegeneratingCaption, setIsRegeneratingCaption] = useState(false);
  const [isRegeneratingImage, setIsRegeneratingImage] = useState(false);
  const [isSchedulingPost, setIsSchedulingPost] = useState(false);
  const [showPlatformDropdown, setShowPlatformDropdown] = useState(false);
  const [showPagesDropdown, setShowPagesDropdown] = useState(false);
  const [editedFacebookPage, setEditedFacebookPage] = useState<string>('');

  // Facebook Comments Modal state
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [commentsModalStep, setCommentsModalStep] = useState<'pages' | 'posts' | 'comments'>('posts');
  const [selectedCommentPageId, setSelectedCommentPageId] = useState<string | null>(null);
  const [selectedCommentPost, setSelectedCommentPost] = useState<FacebookApiPost | null>(null);
  const [publishedPosts, setPublishedPosts] = useState<FacebookApiPost[]>([]);
  const [isLoadingPublishedPosts, setIsLoadingPublishedPosts] = useState(false);
  const [comments, setComments] = useState<FacebookComment[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [commentReplies, setCommentReplies] = useState<Record<string, CommentReply[]>>({});
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [replyingToComment, setReplyingToComment] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isGeneratingReply, setIsGeneratingReply] = useState(false);
  const [isPostingReply, setIsPostingReply] = useState(false);

  // Onboarding state
  const [facebookPages, setFacebookPages] = useState<FacebookPage[]>([]);
  const [instagramAccounts, setInstagramAccounts] = useState<InstagramAccount[]>([]);
  const [selectedFacebookPage, setSelectedFacebookPage] = useState<string>('');
  const [selectedInstagramAccount, setSelectedInstagramAccount] = useState<string>('');
  const [isConnecting, setIsConnecting] = useState<string | null>(null);
  const [showFacebookPageDropdown, setShowFacebookPageDropdown] = useState(false);
  const [showInstagramAccountDropdown, setShowInstagramAccountDropdown] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    peopleReached: 0,
    interactions: 0,
    postsToReview: 0,
  });

  // Settings Panel State
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [settingsActiveSection, setSettingsActiveSection] = useState<string | null>(null);
  const [contentThemes, setContentThemes] = useState<ContentTheme[]>(defaultContentThemes);
  const [customInstructions, setCustomInstructions] = useState('');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);

  // Create from Media State
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaPreviewUrls, setMediaPreviewUrls] = useState<string[]>([]);
  const [mediaInstructions, setMediaInstructions] = useState('');
  const [isGeneratingFromMedia, setIsGeneratingFromMedia] = useState(false);
  const [generatedPostContent, setGeneratedPostContent] = useState('');
  const [generatedPostImage, setGeneratedPostImage] = useState<string | null>(null);
  const [showGeneratedPostSidebar, setShowGeneratedPostSidebar] = useState(false);
  const [generatedPostPlatform, setGeneratedPostPlatform] = useState('linkedin');
  const [generatedPostDate, setGeneratedPostDate] = useState('');
  const [generatedPostTime, setGeneratedPostTime] = useState('');

  // Media Library State
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [mediaLibraryItems, setMediaLibraryItems] = useState<Array<{
    id: string;
    url: string;
    name: string;
    type: 'logo' | 'brain' | 'generated' | 'website';
  }>>([]);
  const [isLoadingMediaLibrary, setIsLoadingMediaLibrary] = useState(false);
  const [selectedLibraryImage, setSelectedLibraryImage] = useState<string | null>(null);

  // Brand Kit Modal State
  const [showBrandKitModal, setShowBrandKitModal] = useState(false);

  // Add Theme Modal State
  const [showAddThemeModal, setShowAddThemeModal] = useState(false);
  const [newThemeName, setNewThemeName] = useState('');
  const [newThemeFrequency, setNewThemeFrequency] = useState('1 post per week');
  const [newThemePlatforms, setNewThemePlatforms] = useState<string[]>(['linkedin']);

  // Load social media settings from backend
  const loadSocialMediaSettings = useCallback(async () => {
    setIsLoadingSettings(true);
    try {
      const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/scheduled-posts/settings${wsParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as APIResponse<{ custom_instructions: string; content_themes: ContentTheme[] }>;
      if (data.success && data.data) {
        if (data.data.custom_instructions) {
          setCustomInstructions(data.data.custom_instructions);
        }
        if (data.data.content_themes && data.data.content_themes.length > 0) {
          setContentThemes(data.data.content_themes);
        }
      }
    } catch (error) {
      console.error('Failed to load social media settings:', error);
    } finally {
      setIsLoadingSettings(false);
    }
  }, [token, currentWorkspace?.id]);

  // Save social media settings to backend
  const saveSocialMediaSettings = async () => {
    setIsSavingSettings(true);
    try {
      const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/scheduled-posts/settings${wsParam}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          custom_instructions: customInstructions,
          content_themes: contentThemes,
          workspace_id: currentWorkspace?.id || null,
        }),
      });
      const data = await response.json() as APIResponse<void>;
      if (data.success) {
        setSettingsActiveSection(null);
      } else {
        console.error('Failed to save settings:', data.error);
      }
    } catch (error) {
      console.error('Failed to save social media settings:', error);
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Add new content theme
  const handleAddTheme = () => {
    if (!newThemeName.trim()) return;

    const newTheme: ContentTheme = {
      id: `theme-${Date.now()}`,
      name: newThemeName.trim(),
      frequency: newThemeFrequency,
      platforms: newThemePlatforms,
      enabled: true,
    };

    setContentThemes(themes => [...themes, newTheme]);
    setNewThemeName('');
    setNewThemeFrequency('1 post per week');
    setNewThemePlatforms(['linkedin']);
    setShowAddThemeModal(false);
  };

  // Load media library items (brand logo, brain images, generated images)
  const loadMediaLibrary = useCallback(async () => {
    setIsLoadingMediaLibrary(true);
    try {
      const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/scheduled-posts/media-library${wsParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as { success: boolean; data?: typeof mediaLibraryItems };
      if (data.success && data.data) {
        setMediaLibraryItems(data.data);
      }
    } catch (error) {
      console.error('Failed to load media library:', error);
    } finally {
      setIsLoadingMediaLibrary(false);
    }
  }, [token, currentWorkspace?.id]);

  // Handle selecting an image from media library
  const handleSelectLibraryImage = async (imageUrl: string) => {
    setSelectedLibraryImage(imageUrl);
    // Convert the URL to a file-like object for the existing flow
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const file = new File([blob], 'library-image.jpg', { type: blob.type || 'image/jpeg' });
      setMediaFiles([file]);
      setMediaPreviewUrls([imageUrl]);
      setShowMediaLibrary(false);
    } catch (error) {
      console.error('Failed to load library image:', error);
      // Fallback: just set the preview URL directly
      setMediaPreviewUrls([imageUrl]);
      setShowMediaLibrary(false);
    }
  };

  // Handle media file upload
  const handleMediaUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles: File[] = [];
    const newPreviews: string[] = [];

    Array.from(files).forEach((file) => {
      if (mediaFiles.length + newFiles.length >= 10) return;
      if (file.size > 10 * 1024 * 1024) return; // Max 10MB

      newFiles.push(file);
      newPreviews.push(URL.createObjectURL(file));
    });

    setMediaFiles((prev) => [...prev, ...newFiles]);
    setMediaPreviewUrls((prev) => [...prev, ...newPreviews]);
  };

  // Remove media file
  const removeMediaFile = (index: number) => {
    URL.revokeObjectURL(mediaPreviewUrls[index]);
    setMediaFiles((prev) => prev.filter((_, i) => i !== index));
    setMediaPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  // Generate post from media
  const generatePostFromMedia = async () => {
    if (mediaFiles.length === 0 && mediaPreviewUrls.length === 0) return;

    setIsGeneratingFromMedia(true);

    try {
      let imageForAI: string;

      // Check if we have a file (user uploaded) or just a URL (from media library)
      if (mediaFiles.length > 0) {
        // Convert the first image file to base64 for the AI to analyze
        const file = mediaFiles[0];

        // Read file as base64 data URL
        const readFileAsBase64 = (file: File): Promise<string> => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        };

        imageForAI = await readFileAsBase64(file);
      } else if (mediaPreviewUrls.length > 0) {
        // Use the URL directly (from media library selection)
        imageForAI = mediaPreviewUrls[0];
      } else {
        throw new Error('No image selected');
      }

      // Get social media settings for the AI prompt
      let aiInstructions = mediaInstructions;
      try {
        const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
        const settingsResponse = await fetch(`${API_URL}/scheduled-posts/settings${wsParam}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (settingsResponse.ok) {
          const settingsData = await settingsResponse.json() as { success: boolean; data?: { custom_instructions?: string } };
          if (settingsData.success && settingsData.data?.custom_instructions) {
            aiInstructions = `${settingsData.data.custom_instructions}\n\nUser's additional instructions: ${mediaInstructions}`;
          }
        }
      } catch (e) {
        // Continue without settings if fetch fails
        console.log('Could not fetch settings, continuing without them');
      }

      // Call AI to generate caption based on the image
      const aiResponse = await fetch(`${API_URL}/scheduled-posts/generate-caption`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image_url: imageForAI,
          instructions: aiInstructions || 'Create an engaging social media post caption for this image.',
          workspace_id: currentWorkspace?.id || null,
        }),
      });

      if (!aiResponse.ok) {
        const errorData = await aiResponse.json().catch(() => ({})) as { error?: string };
        throw new Error(errorData.error || 'Failed to generate caption');
      }

      const aiData = await aiResponse.json() as { success?: boolean; data?: { caption: string }; caption?: string };
      const caption = aiData.data?.caption || aiData.caption || 'Check out this amazing content! #SocialMedia';

      // Set up the generated post
      setGeneratedPostContent(caption);
      setGeneratedPostImage(mediaPreviewUrls[0]); // Use the preview URL for display

      // Set default date/time to now + 1 hour
      const scheduledDate = new Date();
      scheduledDate.setHours(scheduledDate.getHours() + 1);
      setGeneratedPostDate(scheduledDate.toISOString().split('T')[0]);
      setGeneratedPostTime(scheduledDate.toTimeString().slice(0, 5));

      // Close the media modal and show the sidebar
      setShowMediaModal(false);
      setShowCreateModal(false);
      setShowGeneratedPostSidebar(true);
    } catch (error) {
      console.error('Failed to generate post from media:', error);
      alert('Failed to generate post. Please try again.');
    } finally {
      setIsGeneratingFromMedia(false);
    }
  };

  // Schedule the generated post
  const scheduleGeneratedPost = async () => {
    if (!generatedPostContent) return;

    setIsSchedulingPost(true);

    try {
      // Create the scheduled time string in local time format (no timezone conversion)
      // Format: YYYY-MM-DD HH:MM:SS - the backend will store this exactly as the user intended
      const scheduledAtString = `${generatedPostDate} ${generatedPostTime}:00`;
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // If we have an image and mediaFiles (meaning it's a blob URL), upload it first
      let finalImageUrl = generatedPostImage;
      if (mediaFiles.length > 0 && generatedPostImage) {
        try {
          // Convert file to base64
          const file = mediaFiles[0];
          const readFileAsBase64 = (file: File): Promise<string> => {
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
          };

          const base64Image = await readFileAsBase64(file);

          // Upload to get permanent URL
          const uploadResponse = await fetch(`${API_URL}/scheduled-posts/upload-image`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ image_data: base64Image }),
          });

          if (uploadResponse.ok) {
            const uploadData = await uploadResponse.json() as { success: boolean; data?: { url: string } };
            if (uploadData.success && uploadData.data?.url) {
              finalImageUrl = uploadData.data.url;
            }
          }
        } catch (uploadError) {
          console.error('Failed to upload image:', uploadError);
          // Continue with the blob URL as fallback (it might not work for social media posting)
        }
      }

      const response = await fetch(`${API_URL}/scheduled-posts`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: generatedPostContent,
          image_url: finalImageUrl,
          scheduled_at: scheduledAtString,
          timezone: userTimezone,
          target_platforms: [generatedPostPlatform],
          status: 'scheduled', // Needs approval
          workspace_id: currentWorkspace?.id || null,
        }),
      });

      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        // Reset state
        setShowGeneratedPostSidebar(false);
        setGeneratedPostContent('');
        setGeneratedPostImage(null);
        setMediaFiles([]);
        setMediaPreviewUrls([]);
        setMediaInstructions('');

        // Reload posts
        loadScheduledPosts();
      } else {
        alert(`Failed to schedule: ${data.error}`);
      }
    } catch (error) {
      console.error('Failed to schedule post:', error);
      alert('Failed to schedule post. Please try again.');
    } finally {
      setIsSchedulingPost(false);
    }
  };

  // Load integrations
  const loadIntegrations = useCallback(async () => {
    try {
      const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/integrations${wsParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<Integration[]>;
      if (data.success && data.data) {
        const socialMediaIntegrations = data.data.filter(
          (i) => ['facebook', 'instagram', 'linkedin', 'twitter'].includes(i.provider) && i.status === 'connected'
        );
        setIntegrations(socialMediaIntegrations);
        setShowOnboarding(socialMediaIntegrations.length === 0);

        // Load Facebook pages if connected
        const facebookIntegration = data.data.find(i => i.provider === 'facebook' && i.status === 'connected');
        if (facebookIntegration) {
          loadFacebookPages();
        }

        // Load Instagram accounts if connected
        const instagramIntegration = data.data.find(i => i.provider === 'instagram' && i.status === 'connected');
        if (instagramIntegration) {
          loadInstagramAccounts();
        }
      }
    } catch (error) {
      console.error('Failed to load integrations:', error);
    }
  }, [token, currentWorkspace?.id]);

  // Load Facebook pages
  const loadFacebookPages = async () => {
    try {
      const response = await fetch(`${API_URL}/integrations/facebook/pages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<FacebookPage[]>;
      if (data.success && data.data) {
        setFacebookPages(data.data);
        if (data.data.length > 0 && !selectedFacebookPage) {
          setSelectedFacebookPage(data.data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load Facebook pages:', error);
    }
  };

  // Load Instagram accounts
  const loadInstagramAccounts = async () => {
    try {
      const response = await fetch(`${API_URL}/integrations/instagram/accounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<InstagramAccount[]>;
      if (data.success && data.data) {
        setInstagramAccounts(data.data);
      }
    } catch (error) {
      console.error('Failed to load Instagram accounts:', error);
    }
  };

  // Load scheduled posts
  const loadScheduledPosts = useCallback(async () => {
    try {
      const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/scheduled-posts${wsParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<{ posts: ScheduledPost[] }>;
      if (data.success && data.data) {
        setScheduledPosts(data.data.posts || []);
        // Count posts needing approval or scheduled
        const pendingCount = (data.data.posts || []).filter(p => p.status === 'scheduled').length;
        setStats(prev => ({ ...prev, postsToReview: pendingCount }));
      }
    } catch (error) {
      console.error('Failed to load scheduled posts:', error);
    }
  }, [token, currentWorkspace?.id]);

  // Load engagement stats (people reached, interactions, etc.)
  const loadEngagementStats = useCallback(async () => {
    try {
      const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';

      // Fetch stats from scheduled posts (for posts to review count)
      const statsResponse = await fetch(`${API_URL}/scheduled-posts/stats${wsParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const statsData = (await statsResponse.json()) as APIResponse<{
        people_reached: number;
        interactions: number;
        scheduled: number;
      }>;

      // Fetch Facebook insights directly from the API for accurate metrics
      const fbInsightsResponse = await fetch(`${API_URL}/integrations/facebook/insights${wsParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fbInsightsData = (await fbInsightsResponse.json()) as APIResponse<{
        people_reached: number;
        impressions: number;
        interactions: number;
        reactions: number;
        comments: number;
        shares: number;
      }>;

      // Use Facebook insights if available, otherwise fall back to database stats
      const peopleReached = fbInsightsData.success && fbInsightsData.data
        ? fbInsightsData.data.people_reached
        : (statsData.data?.people_reached || 0);

      const interactions = fbInsightsData.success && fbInsightsData.data
        ? fbInsightsData.data.interactions
        : (statsData.data?.interactions || 0);

      setStats(prev => ({
        ...prev,
        peopleReached,
        interactions,
        postsToReview: statsData.data?.scheduled || prev.postsToReview,
      }));
    } catch (error) {
      console.error('Failed to load engagement stats:', error);
    }
  }, [token, currentWorkspace?.id]);

  // Connect integration
  const connectIntegration = async (provider: string) => {
    setIsConnecting(provider);
    try {
      const response = await fetch(`${API_URL}/integrations/connect/${provider}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          redirect_url: '/dashboard/social-media-manager',
          workspace_id: currentWorkspace?.id || null,
        }),
      });

      interface ConnectResponse {
        success: boolean;
        data?: {
          requires_setup?: boolean;
          message?: string;
          auth_url?: string;
        };
        error?: string;
      }

      const data = (await response.json()) as ConnectResponse;
      if (data.success && data.data?.auth_url) {
        window.location.href = data.data.auth_url;
      } else if (data.error) {
        console.error('Connection error:', data.error);
      }
    } catch (error) {
      console.error('Failed to connect:', error);
    } finally {
      setIsConnecting(null);
    }
  };

  // Initialize
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      await loadIntegrations();
      await loadScheduledPosts();
      await loadEngagementStats();
      await loadSocialMediaSettings();
      setIsLoading(false);
    };
    init();
  }, [loadIntegrations, loadScheduledPosts, loadEngagementStats, loadSocialMediaSettings]);

  // Auto-refresh posts to catch status changes (posted/failed)
  // Refresh every 10 seconds if there are posts being actively processed, otherwise every 30 seconds
  useEffect(() => {
    const hasActivelyPostingPosts = scheduledPosts.some(p => p.status === 'posting');
    const refreshIntervalMs = hasActivelyPostingPosts ? 10000 : 30000; // 10s if posting, 30s otherwise

    const refreshInterval = setInterval(async () => {
      try {
        const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
        const response = await fetch(`${API_URL}/scheduled-posts${wsParam}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = (await response.json()) as APIResponse<{ posts: ScheduledPost[] }>;
        if (data.success && data.data) {
          const newPosts = data.data.posts || [];
          setScheduledPosts(newPosts);

          // Update stats
          const pendingCount = newPosts.filter(p => p.status === 'scheduled').length;
          setStats(prev => ({ ...prev, postsToReview: pendingCount }));

          // If a post is currently selected, update it with the latest data
          if (selectedPost) {
            const updatedPost = newPosts.find(p => p.id === selectedPost.id);
            if (updatedPost && updatedPost.status !== selectedPost.status) {
              setSelectedPost(updatedPost);
            }
          }
        }
      } catch (error) {
        // Silently fail - don't interrupt user experience
        console.debug('Auto-refresh failed:', error);
      }
    }, refreshIntervalMs);

    return () => clearInterval(refreshInterval);
  }, [token, selectedPost, scheduledPosts, currentWorkspace?.id]);

  // Populate editing state when post is selected
  useEffect(() => {
    if (selectedPost) {
      setEditedContent(selectedPost.content);
      setEditedPlatform(selectedPost.target_platforms?.[0] || 'linkedin');
      // Parse the scheduled_at string directly without timezone conversion
      // The backend stores it in format: YYYY-MM-DD HH:MM:SS (local time as intended by user)
      const scheduledAtStr = selectedPost.scheduled_at;
      // Handle both formats: "YYYY-MM-DD HH:MM:SS" and ISO format
      const dateTimeParts = scheduledAtStr.includes('T')
        ? scheduledAtStr.split('T')
        : scheduledAtStr.split(' ');
      const datePart = dateTimeParts[0];
      const timePart = dateTimeParts[1]?.slice(0, 5) || '12:00';
      setEditedDate(datePart);
      setEditedTime(timePart);
      // Set Facebook page: use saved page ID, or fallback to selected page, or first page
      if (facebookPages.length > 0) {
        const savedPageId = selectedPost.facebook_page_id;
        // Verify saved page still exists in the list
        const pageExists = savedPageId && facebookPages.some(p => p.id === savedPageId);
        setEditedFacebookPage(pageExists ? savedPageId : (selectedFacebookPage || facebookPages[0].id));
      }
    }
  }, [selectedPost, facebookPages, selectedFacebookPage]);

  // Regenerate caption for sidebar
  const handleSidebarRegenerateCaption = async () => {
    if (!token || !editedContent) return;
    setIsRegeneratingCaption(true);
    try {
      const response = await fetch(`${API_URL}/scheduled-posts/regenerate-caption`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_content: editedContent,
          platform: editedPlatform,
          workspace_id: currentWorkspace?.id || null,
        }),
      });

      interface RegenerateResponse {
        success: boolean;
        data?: { content: string };
        error?: string;
      }
      const data = (await response.json()) as RegenerateResponse;
      if (data.success && data.data) {
        setEditedContent(data.data.content);
      }
    } catch (error) {
      console.error('Failed to regenerate caption:', error);
    } finally {
      setIsRegeneratingCaption(false);
    }
  };

  // Regenerate image for sidebar
  const handleSidebarRegenerateImage = async () => {
    if (!token || !selectedPost) return;
    setIsRegeneratingImage(true);
    try {
      const response = await fetch(`${API_URL}/scheduled-posts/regenerate-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: editedContent,
          platform: editedPlatform,
        }),
      });

      interface RegenerateImageResponse {
        success: boolean;
        data?: { image_url: string };
        error?: string;
      }
      const data = (await response.json()) as RegenerateImageResponse;
      if (data.success && data.data) {
        setSelectedPost(prev => prev ? { ...prev, image_url: data.data!.image_url } : null);
      }
    } catch (error) {
      console.error('Failed to regenerate image:', error);
    } finally {
      setIsRegeneratingImage(false);
    }
  };

  // Open comments modal - decides whether to show page selection or posts directly
  const openCommentsModal = () => {
    // Reset state
    setComments([]);
    setCommentReplies({});
    setExpandedComments(new Set());
    setReplyingToComment(null);
    setReplyText('');
    setSelectedCommentPost(null);
    setSelectedCommentPageId(null);
    setPublishedPosts([]);

    // If multiple Facebook pages, show page selection first
    if (facebookPages.length > 1) {
      setCommentsModalStep('pages');
    } else if (facebookPages.length === 1) {
      // Single page - select it and go to posts
      const pageId = facebookPages[0].id;
      setSelectedCommentPageId(pageId);
      setCommentsModalStep('posts');
      loadPublishedPosts(pageId);
    } else {
      // No pages - still show posts (will show message about no Facebook integration)
      setCommentsModalStep('posts');
    }

    setShowCommentsModal(true);
  };

  // Load published Facebook posts directly from Facebook API
  const loadPublishedPosts = async (pageId?: string) => {
    if (!token) return;
    setIsLoadingPublishedPosts(true);

    try {
      const targetPageId = pageId || selectedCommentPageId;

      if (!targetPageId) {
        // No page selected, can't fetch posts
        setPublishedPosts([]);
        setIsLoadingPublishedPosts(false);
        return;
      }

      const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/integrations/facebook/pages/${targetPageId}/posts${wsParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as { success: boolean; data?: FacebookApiPost[]; error?: string };

      if (data.success && data.data) {
        // Posts are already sorted by created_time descending from Facebook API
        setPublishedPosts(data.data);
      } else {
        console.error('Failed to load posts:', data.error);
        setPublishedPosts([]);
      }
    } catch (error) {
      console.error('Failed to load published posts:', error);
      setPublishedPosts([]);
    } finally {
      setIsLoadingPublishedPosts(false);
    }
  };

  // Select a Facebook page and proceed to posts
  const selectCommentPage = (pageId: string) => {
    setSelectedCommentPageId(pageId);
    setCommentsModalStep('posts');
    loadPublishedPosts(pageId);
  };

  // Select a post and load its comments
  const selectPostForComments = async (post: FacebookApiPost) => {
    setSelectedCommentPost(post);
    setCommentsModalStep('comments');
    await loadComments(post.id);
  };

  // Go back in the comments modal
  const commentsModalGoBack = () => {
    if (commentsModalStep === 'comments') {
      setSelectedCommentPost(null);
      setComments([]);
      setCommentsError(null);
      setCommentsModalStep('posts');
    } else if (commentsModalStep === 'posts' && facebookPages.length > 1) {
      setSelectedCommentPageId(null);
      setCommentsModalStep('pages');
    } else {
      setShowCommentsModal(false);
    }
  };

  // Load Facebook comments for a post (using Facebook post ID)
  const loadComments = async (facebookPostId: string) => {
    if (!token) return;
    setIsLoadingComments(true);
    setComments([]);
    setCommentsError(null);
    setCommentReplies({});
    setExpandedComments(new Set());
    setReplyingToComment(null);
    setReplyText('');

    try {
      const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/integrations/facebook/posts/${facebookPostId}/comments${wsParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as { success: boolean; data?: { comments: FacebookComment[] }; error?: string };

      if (data.success && data.data) {
        setComments(data.data.comments);
      } else if (data.error) {
        // Post might be deleted, private, or inaccessible
        setCommentsError(data.error);
        // Remove this post from the published posts list so user doesn't see it again
        setPublishedPosts(prev => prev.filter(p => p.id !== facebookPostId));
      }
    } catch (error) {
      console.error('Failed to load comments:', error);
      setCommentsError('Failed to load comments. Please try again.');
    } finally {
      setIsLoadingComments(false);
    }
  };

  // Load replies for a specific comment
  const loadReplies = async (commentId: string) => {
    if (!token) return;

    try {
      const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/integrations/facebook/comments/${commentId}/replies${wsParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as { success: boolean; data?: { replies: CommentReply[] } };

      if (data.success && data.data) {
        setCommentReplies(prev => ({ ...prev, [commentId]: data.data!.replies }));
      }
    } catch (error) {
      console.error('Failed to load replies:', error);
    }
  };

  // Toggle expanded state for a comment and load its replies
  const toggleCommentExpanded = async (commentId: string) => {
    const newExpanded = new Set(expandedComments);
    if (newExpanded.has(commentId)) {
      newExpanded.delete(commentId);
    } else {
      newExpanded.add(commentId);
      // Load replies if not already loaded
      if (!commentReplies[commentId]) {
        await loadReplies(commentId);
      }
    }
    setExpandedComments(newExpanded);
  };

  // Generate AI reply for a comment
  const handleGenerateReply = async (comment: FacebookComment) => {
    if (!token || !selectedCommentPost) return;
    setIsGeneratingReply(true);
    setReplyingToComment(comment.id);

    try {
      // Use the AI endpoint to generate a reply based on the comment and post content
      const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/integrations/facebook/comments/generate-reply${wsParam}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          comment_text: comment.message,
          post_content: selectedCommentPost.message,
        }),
      });

      const data = await response.json() as { success: boolean; data?: { reply: string }; error?: string };

      if (data.success && data.data) {
        setReplyText(data.data.reply);
      } else {
        alert(data.error || 'Failed to generate reply');
      }
    } catch (error) {
      console.error('Failed to generate reply:', error);
      alert('Failed to generate reply. Please try again.');
    } finally {
      setIsGeneratingReply(false);
    }
  };

  // Post reply to a comment
  const handlePostReply = async (commentId: string) => {
    if (!token || !selectedCommentPost || !replyText.trim()) return;
    setIsPostingReply(true);

    try {
      const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/integrations/facebook/comments/${commentId}/reply${wsParam}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: replyText.trim() }),
      });

      const data = await response.json() as { success: boolean; data?: { reply_id: string; page_name?: string }; error?: string };

      if (data.success) {
        // Clear reply state
        setReplyingToComment(null);
        setReplyText('');
        // Reload comments to show the new reply
        await loadComments(selectedCommentPost.id);
        // Expand the comment to show the new reply
        setExpandedComments(prev => new Set(prev).add(commentId));
        await loadReplies(commentId);
      } else {
        alert(data.error || 'Failed to post reply');
      }
    } catch (error) {
      console.error('Failed to post reply:', error);
      alert('Failed to post reply. Please try again.');
    } finally {
      setIsPostingReply(false);
    }
  };

  // Cancel reply
  const handleCancelReply = () => {
    setReplyingToComment(null);
    setReplyText('');
  };

  // Format relative time for comments
  const formatCommentTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Schedule/Approve post from sidebar
  const handleApprovePost = async () => {
    if (!token || !selectedPost) return;
    setIsSchedulingPost(true);
    try {
      // Create the scheduled time string in local time format (no timezone conversion)
      const scheduledAtString = `${editedDate} ${editedTime}:00`;
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await fetch(`${API_URL}/scheduled-posts/${selectedPost.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: editedContent,
          target_platforms: [editedPlatform],
          scheduled_at: scheduledAtString,
          timezone: userTimezone,
          status: 'posting',
          facebook_page_id: editedPlatform === 'facebook' ? editedFacebookPage : null,
        }),
      });

      interface UpdateResponse {
        success: boolean;
        error?: string;
      }
      const data = (await response.json()) as UpdateResponse;
      if (data.success) {
        setSelectedPost(null);
        loadScheduledPosts();
      } else {
        alert(`Failed to schedule: ${data.error}`);
      }
    } catch (error) {
      console.error('Failed to approve post:', error);
      alert('Failed to schedule post. Please try again.');
    } finally {
      setIsSchedulingPost(false);
    }
  };

  // Decline/Delete post
  const handleDeclinePost = async () => {
    if (!token || !selectedPost) return;
    if (!confirm('Are you sure you want to decline this post? It will be deleted.')) return;

    try {
      const response = await fetch(`${API_URL}/scheduled-posts/${selectedPost.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      interface DeleteResponse {
        success: boolean;
        error?: string;
      }
      const data = (await response.json()) as DeleteResponse;
      if (data.success) {
        setSelectedPost(null);
        loadScheduledPosts();
      } else {
        alert(`Failed to delete: ${data.error}`);
      }
    } catch (error) {
      console.error('Failed to delete post:', error);
    }
  };

  // Save changes to a scheduled post (for editing after auto-schedule)
  const handleSavePostChanges = async () => {
    if (!token || !selectedPost) return;
    setIsSchedulingPost(true);
    try {
      // Create the scheduled time string in local time format (no timezone conversion)
      const scheduledAtString = `${editedDate} ${editedTime}:00`;
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await fetch(`${API_URL}/scheduled-posts/${selectedPost.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: editedContent,
          target_platforms: [editedPlatform],
          scheduled_at: scheduledAtString,
          timezone: userTimezone,
          // Keep the same status (posting) - just update the content/schedule
          status: selectedPost.status,
          facebook_page_id: editedPlatform === 'facebook' ? editedFacebookPage : null,
        }),
      });

      interface UpdateResponse {
        success: boolean;
        error?: string;
      }
      const data = (await response.json()) as UpdateResponse;
      if (data.success) {
        setSelectedPost(null);
        loadScheduledPosts();
      } else {
        alert(`Failed to save changes: ${data.error}`);
      }
    } catch (error) {
      console.error('Failed to save post changes:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSchedulingPost(false);
    }
  };

  // Cancel a scheduled post (change status back to scheduled for review)
  const handleCancelScheduledPost = async () => {
    if (!token || !selectedPost) return;
    if (!confirm('Are you sure you want to cancel this scheduled post? It will be moved back for review.')) return;

    try {
      const response = await fetch(`${API_URL}/scheduled-posts/${selectedPost.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'cancelled',
        }),
      });

      interface UpdateResponse {
        success: boolean;
        error?: string;
      }
      const data = (await response.json()) as UpdateResponse;
      if (data.success) {
        setSelectedPost(null);
        loadScheduledPosts();
      } else {
        alert(`Failed to cancel: ${data.error}`);
      }
    } catch (error) {
      console.error('Failed to cancel post:', error);
    }
  };

  // Calendar navigation
  const goToPreviousWeek = () => {
    setCurrentWeekStart(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 7);
      return newDate;
    });
  };

  const goToNextWeek = () => {
    setCurrentWeekStart(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 7);
      return newDate;
    });
  };

  // Get days for current two-week view
  const getCalendarDays = () => {
    const days = [];
    for (let i = 0; i < 14; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    return days;
  };

  // Get posts for a specific day
  const getPostsForDay = (date: Date) => {
    return scheduledPosts.filter(post => {
      const postDate = parseScheduledAtAsLocal(post.scheduled_at);
      return postDate.toDateString() === date.toDateString();
    });
  };

  // Format date range for header
  const formatDateRange = () => {
    const endDate = new Date(currentWeekStart);
    endDate.setDate(endDate.getDate() + 13);
    const startMonth = currentWeekStart.toLocaleDateString('en-US', { month: 'short' });
    const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' });
    const startDay = currentWeekStart.getDate();
    const endDay = endDate.getDate();

    if (startMonth === endMonth) {
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
    }
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
  };

  // Check if date is today
  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Get provider icon
  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'facebook':
        return <Facebook className="w-4 h-4" />;
      case 'instagram':
        return <Instagram className="w-4 h-4" />;
      case 'linkedin':
        return <Linkedin className="w-4 h-4" />;
      case 'twitter':
        return <Twitter className="w-4 h-4" />;
      default:
        return null;
    }
  };

  // Get provider color
  const getProviderColor = (provider: string) => {
    switch (provider) {
      case 'facebook':
        return 'bg-blue-600';
      case 'instagram':
        return 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400';
      case 'linkedin':
        return 'bg-blue-700';
      case 'twitter':
        return 'bg-black';
      default:
        return 'bg-gray-500';
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'posted':
      case 'published':
        return 'text-green-400';
      case 'posting':
        return 'text-blue-400';
      case 'scheduled':
        return 'text-amber-400';
      case 'draft':
        return 'text-gray-400';
      case 'failed':
      case 'cancelled':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  // Get status text
  const getStatusText = (status: string) => {
    switch (status) {
      case 'posted':
      case 'published':
        return 'Published';
      case 'posting':
        return 'Scheduled';
      case 'scheduled':
        return 'Needs approval';
      case 'draft':
        return 'Draft';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  // Get status background color for calendar cards (like Sintra.ai)
  const getStatusBgColor = (status: string) => {
    switch (status) {
      case 'posted':
      case 'published':
        return 'bg-green-600';
      case 'posting':
        return 'bg-blue-600';
      case 'scheduled':
        return 'bg-amber-600';
      case 'draft':
        return 'bg-gray-600';
      case 'failed':
      case 'cancelled':
        return 'bg-red-600';
      default:
        return 'bg-gray-600';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-purple"></div>
      </div>
    );
  }

  // Onboarding View
  if (showOnboarding) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-dark-900' : 'bg-white'}`}>
        {/* Header */}
        <div className={`p-6 border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
          <Linkedin className={`w-10 h-10 ${isDark ? 'text-white' : 'text-slate-900'}`} />
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center mb-12">
            <h1 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Connect your social media profiles
            </h1>
            <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
              We'll use this to automatically post content to your social media accounts.
            </p>
          </div>

          {/* Integration Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {socialMediaProviders.map((provider) => {
              const integration = integrations.find(i => i.provider === provider.id);
              const isConnected = !!integration;
              const Icon = provider.icon;

              return (
                <div
                  key={provider.id}
                  className={`rounded-2xl border p-6 ${
                    isDark
                      ? 'bg-dark-800 border-white/10'
                      : 'bg-white border-slate-200 shadow-sm'
                  }`}
                >
                  {/* Header */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-12 h-12 rounded-xl ${provider.color} flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {provider.name}
                    </h3>
                  </div>

                  {/* Content based on connection status */}
                  {isConnected ? (
                    <div className="space-y-3">
                      {/* Facebook Pages Dropdown */}
                      {provider.id === 'facebook' && facebookPages.length > 0 && (
                        <div className="relative">
                          <button
                            onClick={() => setShowFacebookPageDropdown(!showFacebookPageDropdown)}
                            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border ${
                              isDark
                                ? 'bg-dark-700 border-white/10 text-white'
                                : 'bg-slate-50 border-slate-200 text-slate-900'
                            }`}
                          >
                            <span>{facebookPages.find(p => p.id === selectedFacebookPage)?.name || 'Select a page'}</span>
                            <ChevronDown className="w-4 h-4" />
                          </button>
                          {showFacebookPageDropdown && (
                            <div className={`absolute z-10 w-full mt-2 rounded-xl border shadow-lg overflow-hidden ${
                              isDark ? 'bg-dark-700 border-white/10' : 'bg-white border-slate-200'
                            }`}>
                              {facebookPages.map((page) => (
                                <button
                                  key={page.id}
                                  onClick={() => {
                                    setSelectedFacebookPage(page.id);
                                    setShowFacebookPageDropdown(false);
                                  }}
                                  className={`w-full px-4 py-3 text-left flex items-center justify-between ${
                                    isDark ? 'hover:bg-dark-600 text-white' : 'hover:bg-slate-50 text-slate-900'
                                  } ${selectedFacebookPage === page.id ? 'bg-accent-purple/10' : ''}`}
                                >
                                  <span>{page.name}</span>
                                  {selectedFacebookPage === page.id && (
                                    <Check className="w-4 h-4 text-accent-purple" />
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Instagram Accounts Dropdown */}
                      {provider.id === 'instagram' && (
                        <>
                          <div className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border ${
                            isDark
                              ? 'bg-dark-700 border-white/10 text-white'
                              : 'bg-slate-50 border-slate-200 text-slate-900'
                          }`}>
                            <span>{integration.provider_username || integration.provider_email || 'Connected'}</span>
                            <ChevronDown className="w-4 h-4" />
                          </div>
                          {instagramAccounts.length > 0 && (
                            <div className="relative">
                              <button
                                onClick={() => setShowInstagramAccountDropdown(!showInstagramAccountDropdown)}
                                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border ${
                                  isDark
                                    ? 'bg-dark-700 border-white/10 text-white'
                                    : 'bg-slate-50 border-slate-200 text-slate-900'
                                }`}
                              >
                                <span>{instagramAccounts.find(a => a.id === selectedInstagramAccount)?.username || 'Select an Instagram Account'}</span>
                                <ChevronDown className="w-4 h-4" />
                              </button>
                              {showInstagramAccountDropdown && (
                                <div className={`absolute z-10 w-full mt-2 rounded-xl border shadow-lg overflow-hidden ${
                                  isDark ? 'bg-dark-700 border-white/10' : 'bg-white border-slate-200'
                                }`}>
                                  {instagramAccounts.map((account) => (
                                    <button
                                      key={account.id}
                                      onClick={() => {
                                        setSelectedInstagramAccount(account.id);
                                        setShowInstagramAccountDropdown(false);
                                      }}
                                      className={`w-full px-4 py-3 text-left flex items-center justify-between ${
                                        isDark ? 'hover:bg-dark-600 text-white' : 'hover:bg-slate-50 text-slate-900'
                                      } ${selectedInstagramAccount === account.id ? 'bg-accent-purple/10' : ''}`}
                                    >
                                      <span>{account.username}</span>
                                      {selectedInstagramAccount === account.id && (
                                        <Check className="w-4 h-4 text-accent-purple" />
                                      )}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      {/* LinkedIn - just show connected */}
                      {provider.id === 'linkedin' && (
                        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl ${
                          isDark ? 'bg-green-500/10 text-green-400' : 'bg-green-50 text-green-600'
                        }`}>
                          <Check className="w-4 h-4" />
                          <span>Connected</span>
                        </div>
                      )}

                      {/* Twitter/X - show connected with username */}
                      {provider.id === 'twitter' && (
                        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl ${
                          isDark ? 'bg-green-500/10 text-green-400' : 'bg-green-50 text-green-600'
                        }`}>
                          <Check className="w-4 h-4" />
                          <span>{integration.provider_username ? `@${integration.provider_username}` : 'Connected'}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => connectIntegration(provider.id)}
                      disabled={isConnecting === provider.id}
                      className={`w-full py-3 px-4 rounded-xl border font-medium transition-colors ${
                        isDark
                          ? 'border-white/20 text-white hover:bg-white/5'
                          : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                      } ${isConnecting === provider.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isConnecting === provider.id ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Connecting...</span>
                        </div>
                      ) : (
                        'Connect'
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* LinkedIn Organization Card */}
          <div className="max-w-md mx-auto mb-12">
            <div
              className={`rounded-2xl border p-6 ${
                isDark
                  ? 'bg-dark-800 border-white/10'
                  : 'bg-white border-slate-200 shadow-sm'
              }`}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-blue-700 flex items-center justify-center">
                  <Linkedin className="w-6 h-6 text-white" />
                </div>
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  LinkedIn Organization
                </h3>
              </div>
              <button
                className={`w-full py-3 px-4 rounded-xl border font-medium transition-colors ${
                  isDark
                    ? 'border-white/20 text-white hover:bg-white/5'
                    : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                }`}
              >
                Connect
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="flex gap-2">
              <div className={`flex-1 h-1 rounded-full ${integrations.length > 0 ? 'bg-accent-purple' : isDark ? 'bg-dark-700' : 'bg-slate-200'}`} />
              <div className={`flex-1 h-1 rounded-full ${isDark ? 'bg-dark-700' : 'bg-slate-200'}`} />
              <div className={`flex-1 h-1 rounded-full ${isDark ? 'bg-dark-700' : 'bg-slate-200'}`} />
              <div className={`flex-1 h-1 rounded-full ${isDark ? 'bg-dark-700' : 'bg-slate-200'}`} />
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            <button
              onClick={() => navigate('/dashboard/automation')}
              className={`px-6 py-2 font-medium ${isDark ? 'text-white' : 'text-slate-700'}`}
            >
              Back
            </button>
            <button
              onClick={() => {
                if (integrations.length > 0) {
                  setShowOnboarding(false);
                }
              }}
              disabled={integrations.length === 0}
              className={`px-8 py-3 rounded-xl font-medium transition-colors ${
                integrations.length > 0
                  ? 'bg-blue-500 hover:bg-blue-600 text-white'
                  : isDark
                    ? 'bg-dark-700 text-gray-500 cursor-not-allowed'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main Dashboard View
  return (
    <div
      className="min-h-screen relative bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=1920&q=80)',
      }}
    >
      {/* Gradient overlay for better text readability while keeping image visible */}
      <div className={`absolute inset-0 ${isDark ? 'bg-dark-900/60' : 'bg-white/40'} pointer-events-none`} />

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <div className="p-4 sm:p-6">
          <div className="flex items-start sm:items-center justify-between mb-6 sm:mb-8">
            <div className="flex items-center gap-2 sm:gap-4">
              <button
                onClick={() => navigate('/dashboard/automation')}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-black/5 text-slate-900'
                }`}
              >
                <ArrowLeft className="w-5 sm:w-6 h-5 sm:h-6" />
              </button>
              <h1 className={`text-sm sm:text-2xl font-bold whitespace-nowrap ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Social Media Manager
              </h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-1 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 bg-white text-slate-900 rounded-full text-sm sm:text-base font-medium hover:bg-gray-100 transition-colors shadow-lg"
              >
                <Plus className="w-4 sm:w-5 h-4 sm:h-5" />
                <span className="hidden sm:inline">Create content</span>
                <span className="sm:hidden">Create</span>
              </button>
              <button
                onClick={() => setShowSettingsPanel(true)}
                className={`p-2 sm:p-2.5 rounded-full transition-colors ${
                  isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/5 hover:bg-black/10 text-slate-700'
                }`}
              >
                <Settings className="w-4 sm:w-5 h-4 sm:h-5" />
              </button>
            </div>
          </div>

          {/* Action Cards & Stats */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-start gap-4 sm:gap-6 mb-8">
            {/* Posts to Review Card */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="w-full sm:w-48 h-28 sm:h-32 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 p-4 cursor-pointer relative overflow-hidden shadow-lg"
            >
              <div className="flex items-start justify-between mb-2">
                <ThumbsUp className="w-6 h-6 text-white/80" />
                <ExternalLink className="w-4 h-4 text-white/60" />
              </div>
              <p className="text-white font-semibold mt-auto">
                {stats.postsToReview} posts to review
              </p>
            </motion.div>

            {/* Edit Brand Kit Card */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              onClick={() => setShowBrandKitModal(true)}
              className="w-full sm:w-48 h-28 sm:h-32 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 p-4 cursor-pointer relative overflow-hidden shadow-lg"
            >
              <div className="flex items-start justify-between mb-2">
                <Palette className="w-6 h-6 text-white/80" />
                <ExternalLink className="w-4 h-4 text-white/60" />
              </div>
              <p className="text-white font-semibold mt-auto">
                Edit your Brand Kit
              </p>
            </motion.div>

            {/* Spacer - hidden on mobile */}
            <div className="hidden sm:block flex-1" />

            {/* Stats - Clickable to open comments */}
            <button
              onClick={openCommentsModal}
              className={`flex items-center justify-center gap-6 sm:gap-8 px-4 sm:px-6 py-4 rounded-2xl transition-all cursor-pointer ${
                isDark ? 'bg-white/10 backdrop-blur-lg hover:bg-white/15' : 'bg-black/5 backdrop-blur-lg hover:bg-black/10'
              }`}
              title="View & reply to comments"
            >
              <div className="text-center">
                <div className="flex items-center gap-2 justify-center mb-1">
                  <Users className={`w-4 sm:w-5 h-4 sm:h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                  <span className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {stats.peopleReached}
                  </span>
                </div>
                <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>People reached</p>
              </div>
              <div className="text-center">
                <div className="flex items-center gap-2 justify-center mb-1">
                  <TrendingUp className={`w-4 sm:w-5 h-4 sm:h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                  <span className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {stats.interactions}
                  </span>
                </div>
                <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Interactions</p>
              </div>
              <div className="text-center border-l pl-6 sm:pl-8 border-slate-300/30">
                <div className="flex items-center gap-2 justify-center mb-1">
                  <MessageCircle className={`w-4 sm:w-5 h-4 sm:h-5 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
                  <span className={`text-sm font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                    Comments
                  </span>
                </div>
                <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>View & Reply</p>
              </div>
            </button>
          </div>
        </div>

        {/* Calendar Section */}
        <div className={`mx-4 sm:mx-6 rounded-t-3xl ${isDark ? 'bg-dark-800/95' : 'bg-white/95'} backdrop-blur-lg shadow-2xl`}>
          {/* Calendar Header */}
          <div className="flex items-center justify-center gap-4 px-4 sm:px-6 py-4">
            <button
              onClick={goToPreviousWeek}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-black/5 text-slate-700'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className={`text-base sm:text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {formatDateRange()}
            </h2>
            <button
              onClick={goToNextWeek}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-black/5 text-slate-700'
              }`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Calendar Grid - Desktop: 7 columns, Mobile: vertical list */}
          {/* Desktop View */}
          <div className="hidden sm:grid grid-cols-7 gap-px">
            {/* Week 1 */}
            {getCalendarDays().slice(0, 7).map((date, index) => {
              const posts = getPostsForDay(date);
              const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
              const dayNum = date.getDate();
              const today = isToday(date);

              return (
                <div
                  key={index}
                  className={`min-h-[140px] p-3 border-t ${
                    isDark ? 'border-white/5' : 'border-slate-200'
                  } ${index < 6 ? (isDark ? 'border-r border-white/5' : 'border-r border-slate-200') : ''}`}
                >
                  <div className={`text-sm mb-2 ${today ? 'font-bold' : ''} ${
                    isDark ? (today ? 'text-white' : 'text-gray-400') : (today ? 'text-slate-900' : 'text-slate-500')
                  }`}>
                    {dayName} {dayNum}{today && ', Today'}
                  </div>
                  <div className="space-y-2">
                    {posts.map((post) => {
                      const platform = post.target_platforms?.[0] || 'linkedin';
                      return (
                        <motion.div
                          key={post.id}
                          whileHover={{ scale: 1.02 }}
                          onClick={() => setSelectedPost(post)}
                          className={`p-2.5 rounded-lg cursor-pointer ${getStatusBgColor(post.status)}`}
                        >
                          <div className="flex items-center gap-2 mb-1.5">
                            <div className="w-5 h-5 rounded bg-white/20 flex items-center justify-center text-white">
                              {getProviderIcon(platform)}
                            </div>
                          </div>
                          <p className="text-xs font-medium text-white truncate">
                            {stripMarkdown(post.content).substring(0, 18)}...
                          </p>
                          <p className="text-xs text-white/80">
                            {getStatusText(post.status)}
                          </p>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Week 2 */}
            {getCalendarDays().slice(7, 14).map((date, index) => {
              const posts = getPostsForDay(date);
              const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
              const dayNum = date.getDate();
              const today = isToday(date);

              return (
                <div
                  key={index + 7}
                  className={`min-h-[140px] p-3 border-t ${
                    isDark ? 'border-white/5' : 'border-slate-200'
                  } ${index < 6 ? (isDark ? 'border-r border-white/5' : 'border-r border-slate-200') : ''}`}
                >
                  <div className={`text-sm mb-2 ${today ? 'font-bold' : ''} ${
                    isDark ? (today ? 'text-white' : 'text-gray-400') : (today ? 'text-slate-900' : 'text-slate-500')
                  }`}>
                    {dayName} {dayNum}{today && ', Today'}
                  </div>
                  <div className="space-y-2">
                    {posts.map((post) => {
                      const platform = post.target_platforms?.[0] || 'linkedin';
                      return (
                        <motion.div
                          key={post.id}
                          whileHover={{ scale: 1.02 }}
                          onClick={() => setSelectedPost(post)}
                          className={`p-2.5 rounded-lg cursor-pointer ${getStatusBgColor(post.status)}`}
                        >
                          <div className="flex items-center gap-2 mb-1.5">
                            <div className="w-5 h-5 rounded bg-white/20 flex items-center justify-center text-white">
                              {getProviderIcon(platform)}
                            </div>
                          </div>
                          <p className="text-xs font-medium text-white truncate">
                            {stripMarkdown(post.content).substring(0, 18)}...
                          </p>
                          <p className="text-xs text-white/80">
                            {getStatusText(post.status)}
                          </p>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile View - Vertical List */}
          <div className="sm:hidden flex flex-col">
            {getCalendarDays().map((date, index) => {
              const posts = getPostsForDay(date);
              const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
              const dayNum = date.getDate();
              const today = isToday(date);

              return (
                <div
                  key={index}
                  className={`px-4 py-3 border-t ${
                    isDark ? 'border-white/10' : 'border-slate-200'
                  }`}
                >
                  <div className={`text-sm ${today ? 'font-bold' : 'font-medium'} ${
                    isDark ? (today ? 'text-white' : 'text-gray-400') : (today ? 'text-slate-900' : 'text-slate-600')
                  }`}>
                    {dayName} {dayNum}{today && ', Today'}
                  </div>
                  {posts.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {posts.map((post) => {
                        const platform = post.target_platforms?.[0] || 'linkedin';
                        return (
                          <motion.div
                            key={post.id}
                            whileHover={{ scale: 1.02 }}
                            onClick={() => setSelectedPost(post)}
                            className={`p-3 rounded-lg cursor-pointer ${getStatusBgColor(post.status)}`}
                          >
                            <div className="flex items-center gap-2 mb-1.5">
                              <div className="w-5 h-5 rounded bg-white/20 flex items-center justify-center text-white">
                                {getProviderIcon(platform)}
                              </div>
                              <p className="text-xs font-medium text-white truncate flex-1">
                                {stripMarkdown(post.content).substring(0, 30)}...
                              </p>
                            </div>
                            <p className="text-xs text-white/80 pl-7">
                              {getStatusText(post.status)}
                            </p>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Post Detail Sidebar - Sintra.ai Style */}
      <AnimatePresence>
        {selectedPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={() => setSelectedPost(null)}
          >
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className={`absolute right-0 top-0 h-full w-full max-w-md ${
                isDark ? 'bg-dark-800' : 'bg-white'
              } shadow-2xl flex flex-col`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Sidebar Header */}
              <div className={`flex items-center justify-between p-4 border-b ${
                isDark ? 'border-white/10' : 'border-slate-200'
              }`}>
                <button
                  onClick={() => setSelectedPost(null)}
                  className={`p-2 rounded-lg transition-colors ${
                    isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-slate-100 text-slate-500'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Social media post
                </h3>
                <div className="w-9" /> {/* Spacer for centering */}
              </div>

              {/* Scrollable Content Area */}
              <div className="flex-1 overflow-y-auto">
                {/* Post Image */}
                {selectedPost.image_url && (
                  <div className="p-4">
                    <img
                      src={selectedPost.image_url}
                      alt="Post preview"
                      className="w-full rounded-xl object-cover max-h-72"
                    />
                  </div>
                )}

                {/* Regenerate Image / AI Edit buttons */}
                <div className="px-4 pb-4 flex gap-2">
                  <button
                    onClick={handleSidebarRegenerateImage}
                    disabled={isRegeneratingImage}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-full border transition-colors ${
                      isDark
                        ? 'border-white/20 text-white hover:bg-white/5'
                        : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                    } ${isRegeneratingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isRegeneratingImage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    <span className="text-sm font-medium">Regenerate image</span>
                  </button>
                  <button
                    className={`flex items-center justify-center gap-2 py-2.5 px-4 rounded-full border transition-colors ${
                      isDark
                        ? 'border-white/20 text-white hover:bg-white/5'
                        : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-medium">AI Edit</span>
                  </button>
                </div>

                {/* Content Section */}
                <div className="px-4 pb-4">
                  <p className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Content</p>
                  <div className={`p-4 rounded-xl max-h-48 overflow-y-auto ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                    <div className={`whitespace-pre-wrap text-sm prose prose-sm max-w-none ${isDark ? 'text-white prose-invert' : 'text-slate-900'}`}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                          h1: ({ children }) => <strong className="block text-base mb-1">{children}</strong>,
                          h2: ({ children }) => <strong className="block text-base mb-1">{children}</strong>,
                          h3: ({ children }) => <strong className="block mb-1">{children}</strong>,
                          h4: ({ children }) => <strong className="block mb-1">{children}</strong>,
                          ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-0.5">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-0.5">{children}</ol>,
                          li: ({ children }) => <li>{children}</li>,
                          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                        }}
                      >
                        {editedContent}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>

                {/* Regenerate Caption / AI Edit buttons */}
                <div className="px-4 pb-4 flex gap-2">
                  <button
                    onClick={handleSidebarRegenerateCaption}
                    disabled={isRegeneratingCaption}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-full border transition-colors ${
                      isDark
                        ? 'border-white/20 text-white hover:bg-white/5'
                        : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                    } ${isRegeneratingCaption ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isRegeneratingCaption ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    <span className="text-sm font-medium">Regenerate caption</span>
                  </button>
                  <button
                    className={`flex items-center justify-center gap-2 py-2.5 px-4 rounded-full border transition-colors ${
                      isDark
                        ? 'border-white/20 text-white hover:bg-white/5'
                        : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-medium">AI Edit</span>
                  </button>
                </div>

                {/* Publish Date */}
                <div className="px-4 pb-4">
                  <p className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Publish date</p>
                  <div className={`flex items-center justify-between p-3 rounded-xl border ${
                    isDark ? 'border-white/10 bg-dark-700' : 'border-slate-200 bg-slate-50'
                  }`}>
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={editedDate}
                        onChange={(e) => setEditedDate(e.target.value)}
                        className={`bg-transparent border-none outline-none ${isDark ? 'text-white' : 'text-slate-900'}`}
                      />
                      <span className={isDark ? 'text-gray-400' : 'text-slate-500'}>at</span>
                      <input
                        type="time"
                        value={editedTime}
                        onChange={(e) => setEditedTime(e.target.value)}
                        className={`bg-transparent border-none outline-none ${isDark ? 'text-white' : 'text-slate-900'}`}
                      />
                    </div>
                    <Calendar className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-400'}`} />
                  </div>
                </div>

                {/* Platform Dropdown */}
                <div className="px-4 pb-4">
                  <p className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Platform</p>
                  <div className="relative">
                    <button
                      onClick={() => setShowPlatformDropdown(!showPlatformDropdown)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border ${
                        isDark ? 'border-white/10 bg-dark-700' : 'border-slate-200 bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded flex items-center justify-center text-white ${getProviderColor(editedPlatform)}`}>
                          {getProviderIcon(editedPlatform)}
                        </div>
                        <span className={`capitalize ${isDark ? 'text-white' : 'text-slate-900'}`}>{editedPlatform}</span>
                      </div>
                      <ChevronDown className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-slate-400'}`} />
                    </button>
                    {showPlatformDropdown && (
                      <div className={`absolute z-10 w-full mt-2 rounded-xl border shadow-lg overflow-hidden ${
                        isDark ? 'bg-dark-700 border-white/10' : 'bg-white border-slate-200'
                      }`}>
                        {['linkedin', 'facebook', 'instagram', 'twitter'].map((platform) => (
                          <button
                            key={platform}
                            onClick={() => {
                              setEditedPlatform(platform);
                              setShowPlatformDropdown(false);
                            }}
                            className={`w-full px-4 py-3 text-left flex items-center gap-2 ${
                              isDark ? 'hover:bg-dark-600 text-white' : 'hover:bg-slate-50 text-slate-900'
                            } ${editedPlatform === platform ? 'bg-accent-purple/10' : ''}`}
                          >
                            <div className={`w-6 h-6 rounded flex items-center justify-center text-white ${getProviderColor(platform)}`}>
                              {getProviderIcon(platform)}
                            </div>
                            <span className="capitalize">{platform}</span>
                            {editedPlatform === platform && (
                              <Check className="w-4 h-4 text-accent-purple ml-auto" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Pages Dropdown - Show Facebook pages for Facebook platform */}
                <div className="px-4 pb-4">
                  <p className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Pages</p>
                  <div className="relative">
                    <button
                      onClick={() => setShowPagesDropdown(!showPagesDropdown)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border ${
                        isDark ? 'border-white/10 bg-dark-700' : 'border-slate-200 bg-slate-50'
                      }`}
                    >
                      <span className={isDark ? 'text-white' : 'text-slate-900'}>
                        {editedPlatform === 'facebook' && facebookPages.length > 0
                          ? facebookPages.find(p => p.id === editedFacebookPage)?.name || 'Select a page'
                          : integrations.find(i => i.provider === editedPlatform)?.provider_username ||
                            integrations.find(i => i.provider === editedPlatform)?.provider_email ||
                            'Select account'}
                      </span>
                      <ChevronDown className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-slate-400'}`} />
                    </button>
                    {showPagesDropdown && (
                      <div className={`absolute z-10 w-full mt-2 rounded-xl border shadow-lg overflow-hidden ${
                        isDark ? 'bg-dark-700 border-white/10' : 'bg-white border-slate-200'
                      }`}>
                        {/* Show Facebook pages when platform is Facebook */}
                        {editedPlatform === 'facebook' && facebookPages.length > 0 ? (
                          facebookPages.map((page) => (
                            <button
                              key={page.id}
                              onClick={() => {
                                setEditedFacebookPage(page.id);
                                setShowPagesDropdown(false);
                              }}
                              className={`w-full px-4 py-3 text-left flex items-center justify-between ${
                                isDark ? 'hover:bg-dark-600 text-white' : 'hover:bg-slate-50 text-slate-900'
                              } ${editedFacebookPage === page.id ? 'bg-accent-purple/10' : ''}`}
                            >
                              <span>{page.name}</span>
                              {editedFacebookPage === page.id && (
                                <Check className="w-4 h-4 text-accent-purple" />
                              )}
                            </button>
                          ))
                        ) : (
                          <>
                            {integrations.filter(i => i.provider === editedPlatform).map((integration) => (
                              <button
                                key={integration.id}
                                onClick={() => setShowPagesDropdown(false)}
                                className={`w-full px-4 py-3 text-left ${
                                  isDark ? 'hover:bg-dark-600 text-white' : 'hover:bg-slate-50 text-slate-900'
                                }`}
                              >
                                {integration.provider_username || integration.provider_email || 'Connected Account'}
                              </button>
                            ))}
                            {integrations.filter(i => i.provider === editedPlatform).length === 0 && (
                              <p className={`px-4 py-3 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                No accounts connected for {editedPlatform}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Fixed Footer with Action Buttons */}
              <div className={`p-4 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                {selectedPost.status === 'scheduled' && (
                  <div className="flex gap-3">
                    <button
                      onClick={handleDeclinePost}
                      className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
                        isDark
                          ? 'bg-white/10 hover:bg-white/20 text-white'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                      }`}
                    >
                      Decline
                    </button>
                    <button
                      onClick={handleApprovePost}
                      disabled={isSchedulingPost}
                      className={`flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors ${
                        isSchedulingPost ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {isSchedulingPost ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Scheduling...</span>
                        </div>
                      ) : (
                        'Schedule'
                      )}
                    </button>
                  </div>
                )}
                {selectedPost.status === 'posting' && (
                  <div className="space-y-3">
                    <div className={`p-4 rounded-xl text-center ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                      <Check className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                      <p className={`font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                        This post is scheduled and will be published automatically
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={handleCancelScheduledPost}
                        className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
                          isDark
                            ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400'
                            : 'bg-red-50 hover:bg-red-100 text-red-600'
                        }`}
                      >
                        Cancel Post
                      </button>
                      <button
                        onClick={handleSavePostChanges}
                        disabled={isSchedulingPost}
                        className={`flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors ${
                          isSchedulingPost ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {isSchedulingPost ? (
                          <div className="flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Saving...</span>
                          </div>
                        ) : (
                          'Save Changes'
                        )}
                      </button>
                    </div>
                  </div>
                )}
                {selectedPost.status === 'posted' && (
                  <div className={`p-4 rounded-xl text-center ${isDark ? 'bg-green-500/10' : 'bg-green-50'}`}>
                    <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className={`font-medium ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                      This post has been published
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Facebook Comments Modal */}
      <AnimatePresence>
        {showCommentsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCommentsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`w-full max-w-2xl max-h-[90vh] rounded-2xl ${isDark ? 'bg-dark-800' : 'bg-white'} shadow-2xl flex flex-col`}
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                <div className="flex items-center gap-3">
                  {commentsModalStep !== 'pages' && (commentsModalStep !== 'posts' || facebookPages.length > 1) && (
                    <button
                      onClick={commentsModalGoBack}
                      className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                    >
                      <ChevronLeft className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                    </button>
                  )}
                  <div className="flex items-center gap-2">
                    <MessageCircle className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {commentsModalStep === 'pages' && 'Select Facebook Page'}
                      {commentsModalStep === 'posts' && 'Select Post'}
                      {commentsModalStep === 'comments' && 'Comments'}
                    </h2>
                  </div>
                </div>
                <button
                  onClick={() => setShowCommentsModal(false)}
                  className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                >
                  <X className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {/* Step 1: Page Selection (if multiple pages) */}
                {commentsModalStep === 'pages' && (
                  <div className="space-y-2">
                    <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      Select a Facebook page to view comments
                    </p>
                    {facebookPages.map((page) => (
                      <button
                        key={page.id}
                        onClick={() => selectCommentPage(page.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                          isDark
                            ? 'border-white/10 hover:bg-white/5'
                            : 'border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                          <Facebook className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                        </div>
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {page.name}
                        </span>
                        <ChevronRight className={`w-5 h-5 ml-auto ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                      </button>
                    ))}
                  </div>
                )}

                {/* Step 2: Post Selection */}
                {commentsModalStep === 'posts' && (
                  <div className="space-y-2">
                    {isLoadingPublishedPosts ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className={`w-8 h-8 animate-spin ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      </div>
                    ) : publishedPosts.length === 0 ? (
                      <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                        <Facebook className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p className="font-medium mb-1">No published Facebook posts</p>
                        <p className="text-sm">Posts you publish to Facebook will appear here</p>
                      </div>
                    ) : (
                      <>
                        <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                          Select a post to view and reply to comments
                        </p>
                        {publishedPosts.map((post) => (
                          <button
                            key={post.id}
                            onClick={() => selectPostForComments(post)}
                            className={`w-full text-left p-3 rounded-xl border transition-colors ${
                              isDark
                                ? 'border-white/10 hover:bg-white/5'
                                : 'border-slate-200 hover:bg-slate-50'
                            }`}
                          >
                            <div className="flex gap-3">
                              {post.full_picture && (
                                <img
                                  src={post.full_picture}
                                  alt=""
                                  className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm line-clamp-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                  {post.message || '(No text content)'}
                                </p>
                                <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                                  Posted {formatCommentTime(post.created_time)}
                                </p>
                              </div>
                              <ChevronRight className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                            </div>
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                )}

                {/* Step 3: Comments View */}
                {commentsModalStep === 'comments' && selectedCommentPost && (
                  <div className="space-y-4">
                    {/* Post Preview */}
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                      <div className="flex gap-3">
                        {selectedCommentPost.full_picture && (
                          <img
                            src={selectedCommentPost.full_picture}
                            alt=""
                            className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                          />
                        )}
                        <p className={`text-sm line-clamp-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                          {selectedCommentPost.message || '(No text content)'}
                        </p>
                      </div>
                    </div>

                    {/* Comments List */}
                    {isLoadingComments ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className={`w-6 h-6 animate-spin ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      </div>
                    ) : commentsError ? (
                      <div className={`text-center py-8 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                        <AlertCircle className={`w-10 h-10 mx-auto mb-2 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
                        <p className="font-medium">Post Unavailable</p>
                        <p className="text-sm mb-3">This post may have been deleted or set to private.</p>
                        <button
                          onClick={commentsModalGoBack}
                          className={`px-4 py-2 rounded-lg text-sm font-medium ${
                            isDark
                              ? 'bg-white/10 hover:bg-white/20 text-white'
                              : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                          }`}
                        >
                          Back to Posts
                        </button>
                      </div>
                    ) : comments.length === 0 ? (
                      <div className={`text-center py-8 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                        <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p className="font-medium">No comments yet</p>
                        <p className="text-sm">Comments on this post will appear here</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                            {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
                          </span>
                          <button
                            onClick={() => loadComments(selectedCommentPost.id)}
                            disabled={isLoadingComments}
                            className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                          >
                            <RefreshCw className={`w-4 h-4 ${isLoadingComments ? 'animate-spin' : ''} ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                          </button>
                        </div>

                        {comments.map((comment) => (
                          <div
                            key={comment.id}
                            className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}
                          >
                            <div className="flex gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-white/10' : 'bg-slate-200'}`}>
                                <User className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                    {comment.author_name}
                                  </span>
                                  <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                                    {formatCommentTime(comment.created_at)}
                                  </span>
                                </div>
                                <p className={`text-sm mt-1 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                                  {comment.message}
                                </p>

                                {/* Actions */}
                                <div className="flex items-center gap-3 mt-2">
                                  <button
                                    onClick={() => handleGenerateReply(comment)}
                                    disabled={isGeneratingReply && replyingToComment === comment.id}
                                    className={`flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-lg transition-colors ${
                                      isDark
                                        ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                                        : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                                    } ${isGeneratingReply && replyingToComment === comment.id ? 'opacity-50' : ''}`}
                                  >
                                    {isGeneratingReply && replyingToComment === comment.id ? (
                                      <>
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Generating...
                                      </>
                                    ) : (
                                      <>
                                        <Sparkles className="w-3 h-3" />
                                        AI Reply
                                      </>
                                    )}
                                  </button>
                                  {comment.reply_count > 0 && (
                                    <button
                                      onClick={() => toggleCommentExpanded(comment.id)}
                                      className={`text-xs ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-slate-500 hover:text-slate-700'}`}
                                    >
                                      {expandedComments.has(comment.id) ? 'Hide' : 'View'} {comment.reply_count} {comment.reply_count === 1 ? 'reply' : 'replies'}
                                    </button>
                                  )}
                                </div>

                                {/* Existing Replies */}
                                {expandedComments.has(comment.id) && commentReplies[comment.id] && (
                                  <div className={`mt-3 pl-3 border-l-2 ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                                    {commentReplies[comment.id].map((reply) => (
                                      <div key={reply.id} className="py-2">
                                        <div className="flex items-center gap-2">
                                          <span className={`font-medium text-xs ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                            {reply.author_name}
                                          </span>
                                          <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                                            {formatCommentTime(reply.created_at)}
                                          </span>
                                        </div>
                                        <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                                          {reply.message}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Reply Input */}
                                {replyingToComment === comment.id && (
                                  <div className={`mt-3 p-3 rounded-lg ${isDark ? 'bg-dark-700' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                                    <textarea
                                      value={replyText}
                                      onChange={(e) => setReplyText(e.target.value)}
                                      placeholder="Write your reply..."
                                      rows={3}
                                      className={`w-full text-sm resize-none border-0 p-0 focus:ring-0 ${
                                        isDark
                                          ? 'bg-transparent text-white placeholder-gray-500'
                                          : 'bg-transparent text-slate-900 placeholder-slate-400'
                                      }`}
                                    />
                                    <div className="flex items-center justify-between mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-slate-100'}">
                                      <button
                                        onClick={handleCancelReply}
                                        className={`text-xs px-3 py-1.5 rounded-lg ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`}
                                      >
                                        Cancel
                                      </button>
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={() => handleGenerateReply(comment)}
                                          disabled={isGeneratingReply}
                                          className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors ${
                                            isDark
                                              ? 'bg-white/10 hover:bg-white/20 text-white'
                                              : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                                          } ${isGeneratingReply ? 'opacity-50' : ''}`}
                                        >
                                          <Sparkles className="w-3 h-3" />
                                          Regenerate
                                        </button>
                                        <button
                                          onClick={() => handlePostReply(comment.id)}
                                          disabled={isPostingReply || !replyText.trim()}
                                          className={`flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors ${
                                            (isPostingReply || !replyText.trim()) ? 'opacity-50 cursor-not-allowed' : ''
                                          }`}
                                        >
                                          {isPostingReply ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                          ) : (
                                            <Send className="w-3 h-3" />
                                          )}
                                          Post Reply
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Content Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`w-full max-w-md rounded-2xl p-6 ${isDark ? 'bg-dark-800' : 'bg-white'} shadow-2xl`}
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Create content
                </h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                >
                  <X className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                </button>
              </div>

              <div className="space-y-3">
                {/* Create with AI Employee */}
                {socialMediaEmployees.map(employee => (
                  <button
                    key={employee.id}
                    onClick={() => {
                      setShowCreateModal(false);
                      // Navigate to employee chat with autopost param
                      navigate(`/dashboard/employees/${employee.id}?autopost=true`);
                    }}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-colors ${
                      isDark
                        ? 'border-white/10 hover:bg-white/5'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center text-2xl`}>
                      {employee.avatar}
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Create with {employee.name}
                      </h3>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                        Talk to your {employee.role} and create posts together.
                      </p>
                    </div>
                    <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-400'}`} />
                  </button>
                ))}

                {/* Create from Media */}
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowMediaModal(true);
                  }}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-colors ${
                    isDark
                      ? 'border-white/10 hover:bg-white/5'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl ${isDark ? 'bg-orange-500/20' : 'bg-orange-100'} flex items-center justify-center`}>
                    <ImageIcon className={`w-6 h-6 ${isDark ? 'text-orange-400' : 'text-orange-500'}`} />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      Create from media
                    </h3>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                      Add your own media — AI will turn it into ready-to-post content.
                    </p>
                  </div>
                  <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-400'}`} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Settings Panel - Slide from right */}
      <AnimatePresence>
        {showSettingsPanel && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-40"
              onClick={() => {
                setShowSettingsPanel(false);
                setSettingsActiveSection(null);
              }}
            />
            {/* Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className={`fixed top-0 right-0 h-full w-full max-w-md z-50 ${
                isDark ? 'bg-dark-800' : 'bg-white'
              } shadow-2xl flex flex-col`}
            >
              {/* Panel Header */}
              <div className={`flex items-center justify-between p-4 border-b ${
                isDark ? 'border-white/10' : 'border-slate-200'
              }`}>
                {settingsActiveSection ? (
                  <button
                    onClick={() => setSettingsActiveSection(null)}
                    className={`p-1 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                  >
                    <ChevronLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-slate-700'}`} />
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setShowSettingsPanel(false);
                      setSettingsActiveSection(null);
                    }}
                    className={`p-1 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                  >
                    <X className={`w-5 h-5 ${isDark ? 'text-white' : 'text-slate-700'}`} />
                  </button>
                )}
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {settingsActiveSection === 'themes' ? 'Content themes' :
                   settingsActiveSection === 'instructions' ? 'Custom instructions' :
                   settingsActiveSection === 'platforms' ? 'Connected platforms' :
                   settingsActiveSection === 'how-it-works' ? 'How it works' :
                   'Settings'}
                </h2>
                <div className="w-7" /> {/* Spacer for centering */}
              </div>

              {/* Panel Content */}
              <div className="flex-1 overflow-y-auto">
                {!settingsActiveSection ? (
                  /* Main Settings Menu */
                  <div className="p-4 space-y-2">
                    {/* Content themes */}
                    <button
                      onClick={() => setSettingsActiveSection('themes')}
                      className={`w-full flex items-center justify-between p-4 rounded-xl transition-colors ${
                        isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
                      }`}
                    >
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Content themes
                      </span>
                      <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-400'}`} />
                    </button>

                    {/* Custom instructions */}
                    <button
                      onClick={() => setSettingsActiveSection('instructions')}
                      className={`w-full flex items-center justify-between p-4 rounded-xl transition-colors ${
                        isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
                      }`}
                    >
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Custom instructions
                      </span>
                      <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-400'}`} />
                    </button>

                    {/* Connected platforms */}
                    <button
                      onClick={() => setSettingsActiveSection('platforms')}
                      className={`w-full flex items-center justify-between p-4 rounded-xl transition-colors ${
                        isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
                      }`}
                    >
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Connected platforms
                      </span>
                      <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-400'}`} />
                    </button>

                    {/* Divider */}
                    <div className={`my-4 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`} />

                    {/* How it works */}
                    <button
                      onClick={() => setSettingsActiveSection('how-it-works')}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl transition-colors ${
                        isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
                      }`}
                    >
                      <HelpCircle className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        How it works
                      </span>
                      <div className="flex-1" />
                      <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-400'}`} />
                    </button>

                    {/* Setup from scratch */}
                    <button
                      onClick={() => setShowOnboarding(true)}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl transition-colors ${
                        isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
                      }`}
                    >
                      <Zap className={`w-5 h-5 text-orange-500`} />
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Setup from scratch
                      </span>
                    </button>
                  </div>
                ) : settingsActiveSection === 'themes' ? (
                  /* Content Themes Section */
                  <div className="p-4 space-y-3">
                    {contentThemes.map((theme) => (
                      <div
                        key={theme.id}
                        className={`p-4 rounded-xl border ${
                          theme.enabled
                            ? isDark ? 'border-purple-500/50 bg-purple-500/10' : 'border-purple-300 bg-purple-50'
                            : isDark ? 'border-white/10' : 'border-slate-200'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <button
                            onClick={() => {
                              setContentThemes(themes =>
                                themes.map(t =>
                                  t.id === theme.id ? { ...t, enabled: !t.enabled } : t
                                )
                              );
                            }}
                            className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                              theme.enabled
                                ? 'bg-purple-500 border-purple-500'
                                : isDark ? 'border-gray-500' : 'border-slate-300'
                            }`}
                          >
                            {theme.enabled && <Check className="w-3 h-3 text-white" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {theme.name}
                            </h4>
                            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                              {theme.frequency} on {theme.platforms.join(', ')}
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              setContentThemes(themes => themes.filter(t => t.id !== theme.id));
                            }}
                            className={`p-1.5 rounded-lg transition-colors ${
                              isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-slate-100 text-slate-400'
                            }`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* Add Theme Button */}
                    <button
                      onClick={() => setShowAddThemeModal(true)}
                      className={`w-full flex items-center gap-2 p-4 rounded-xl border border-dashed transition-colors ${
                        isDark ? 'border-white/20 hover:border-white/40 text-gray-400' : 'border-slate-300 hover:border-slate-400 text-slate-500'
                      }`}
                    >
                      <Plus className="w-5 h-5" />
                      <span>Add</span>
                    </button>

                    {/* Add Theme Modal */}
                    <AnimatePresence>
                      {showAddThemeModal && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
                          onClick={() => setShowAddThemeModal(false)}
                        >
                          <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className={`w-full max-w-md rounded-2xl ${isDark ? 'bg-dark-800' : 'bg-white'} shadow-2xl overflow-hidden`}
                            onClick={e => e.stopPropagation()}
                          >
                            {/* Modal Header */}
                            <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                Add Content Theme
                              </h3>
                              <button
                                onClick={() => setShowAddThemeModal(false)}
                                className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                              >
                                <X className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                              </button>
                            </div>

                            {/* Modal Content */}
                            <div className="p-4 space-y-4">
                              {/* Theme Name */}
                              <div>
                                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                                  Theme name
                                </label>
                                <input
                                  type="text"
                                  value={newThemeName}
                                  onChange={(e) => setNewThemeName(e.target.value)}
                                  placeholder="e.g., Product Updates & Announcements"
                                  className={`w-full px-4 py-3 rounded-xl border ${
                                    isDark
                                      ? 'bg-dark-700 border-white/10 text-white placeholder-gray-500'
                                      : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                                  }`}
                                />
                              </div>

                              {/* Frequency */}
                              <div>
                                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                                  Posting frequency
                                </label>
                                <select
                                  value={newThemeFrequency}
                                  onChange={(e) => setNewThemeFrequency(e.target.value)}
                                  className={`w-full px-4 py-3 rounded-xl border ${
                                    isDark
                                      ? 'bg-dark-700 border-white/10 text-white'
                                      : 'bg-slate-50 border-slate-200 text-slate-900'
                                  }`}
                                >
                                  <option value="1 post per day">1 post per day</option>
                                  <option value="2-3 posts per week">2-3 posts per week</option>
                                  <option value="1-2 posts per week">1-2 posts per week</option>
                                  <option value="1 post per week">1 post per week</option>
                                  <option value="1 post every 2 weeks">1 post every 2 weeks</option>
                                  <option value="1 post every 10 days">1 post every 10 days</option>
                                  <option value="1 post per month">1 post per month</option>
                                </select>
                              </div>

                              {/* Platforms */}
                              <div>
                                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                                  Platforms
                                </label>
                                <div className="flex flex-wrap gap-2">
                                  {[
                                    { id: 'facebook', label: 'Facebook', Icon: Facebook },
                                    { id: 'instagram', label: 'Instagram', Icon: Instagram },
                                    { id: 'linkedin', label: 'LinkedIn', Icon: Linkedin },
                                  ].map(({ id, label, Icon }) => (
                                    <button
                                      key={id}
                                      onClick={() => {
                                        setNewThemePlatforms(platforms =>
                                          platforms.includes(id)
                                            ? platforms.filter(p => p !== id)
                                            : [...platforms, id]
                                        );
                                      }}
                                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                                        newThemePlatforms.includes(id)
                                          ? 'bg-purple-500 border-purple-500 text-white'
                                          : isDark
                                            ? 'border-white/20 text-gray-400 hover:border-white/40'
                                            : 'border-slate-300 text-slate-600 hover:border-slate-400'
                                      }`}
                                    >
                                      <Icon className="w-4 h-4" />
                                      <span className="text-sm">{label}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>

                            {/* Modal Footer */}
                            <div className={`flex gap-3 p-4 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                              <button
                                onClick={() => setShowAddThemeModal(false)}
                                className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
                                  isDark
                                    ? 'bg-white/10 text-white hover:bg-white/20'
                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleAddTheme}
                                disabled={!newThemeName.trim() || newThemePlatforms.length === 0}
                                className="flex-1 py-3 px-4 bg-purple-500 hover:bg-purple-600 text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Add Theme
                              </button>
                            </div>
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : settingsActiveSection === 'instructions' ? (
                  /* Custom Instructions Section */
                  <div className="p-4">
                    <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                      Custom instructions
                    </p>
                    <textarea
                      value={customInstructions}
                      onChange={(e) => setCustomInstructions(e.target.value)}
                      placeholder="Use hashtags, don't use emojis, etc."
                      className={`w-full h-32 p-3 rounded-xl border resize-none ${
                        isDark
                          ? 'bg-dark-700 border-white/10 text-white placeholder-gray-500'
                          : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                      }`}
                    />
                    <p className={`text-sm mt-3 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                      Your AI employee will follow these instructions when creating new posts.
                    </p>
                  </div>
                ) : settingsActiveSection === 'platforms' ? (
                  /* Connected Platforms Section */
                  <div className="p-4 space-y-4">
                    {socialMediaProviders.map((provider) => {
                      const integration = integrations.find(i => i.provider === provider.id);
                      const Icon = provider.icon;
                      return (
                        <div key={provider.id}>
                          <h4 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                            {provider.name}
                          </h4>
                          {integration ? (
                            <div className={`flex items-center gap-3 p-3 rounded-xl ${
                              isDark ? 'bg-white/5' : 'bg-slate-50'
                            }`}>
                              <div className={`w-5 h-5 rounded-full bg-green-500 flex items-center justify-center`}>
                                <Check className="w-3 h-3 text-white" />
                              </div>
                              <span className={isDark ? 'text-white' : 'text-slate-900'}>
                                {integration.provider_email || integration.provider_username || 'Connected'}
                              </span>
                            </div>
                          ) : (
                            <button
                              onClick={() => connectIntegration(provider.id)}
                              disabled={isConnecting === provider.id}
                              className={`w-full flex items-center justify-center gap-2 p-3 rounded-xl transition-colors ${
                                isDark
                                  ? 'bg-white/10 hover:bg-white/20 text-white'
                                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                              }`}
                            >
                              {isConnecting === provider.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Icon className="w-4 h-4" />
                                  <span>Connect {provider.name}</span>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : settingsActiveSection === 'how-it-works' ? (
                  /* How It Works Section */
                  <div className="p-4 space-y-4">
                    <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'
                        }`}>
                          1
                        </div>
                        <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          Set up content themes
                        </h4>
                      </div>
                      <p className={`text-sm ml-11 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                        Choose the types of content you want to post and how often.
                      </p>
                    </div>

                    <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'
                        }`}>
                          2
                        </div>
                        <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          AI creates posts
                        </h4>
                      </div>
                      <p className={`text-sm ml-11 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                        Your AI employee will automatically generate posts based on your themes.
                      </p>
                    </div>

                    <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'
                        }`}>
                          3
                        </div>
                        <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          Review & approve
                        </h4>
                      </div>
                      <p className={`text-sm ml-11 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                        Review generated posts and approve them for publishing.
                      </p>
                    </div>

                    <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'
                        }`}>
                          4
                        </div>
                        <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          Auto-publish
                        </h4>
                      </div>
                      <p className={`text-sm ml-11 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                        Approved posts are automatically published at the scheduled time.
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Save Button - only show in subsections */}
              {settingsActiveSection && settingsActiveSection !== 'how-it-works' && (
                <div className={`p-4 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                  <button
                    onClick={saveSocialMediaSettings}
                    disabled={isSavingSettings}
                    className="w-full py-3 px-4 bg-purple-500 hover:bg-purple-600 text-white font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isSavingSettings ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Create from Media Modal */}
      <AnimatePresence>
        {showMediaModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => {
              setShowMediaModal(false);
              setMediaFiles([]);
              setMediaPreviewUrls([]);
              setMediaInstructions('');
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`w-full max-w-lg rounded-2xl ${isDark ? 'bg-dark-800' : 'bg-white'} shadow-2xl overflow-hidden`}
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={`flex items-center justify-between p-4 border-b ${
                isDark ? 'border-white/10' : 'border-slate-200'
              }`}>
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Create from media
                </h2>
                <button
                  onClick={() => {
                    setShowMediaModal(false);
                    setMediaFiles([]);
                    setMediaPreviewUrls([]);
                    setMediaInstructions('');
                  }}
                  className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                >
                  <X className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-5">
                {/* Upload Area */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                    Upload media
                  </label>
                  <div
                    className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                      mediaFiles.length > 0
                        ? isDark ? 'border-purple-500/50 bg-purple-500/10' : 'border-purple-300 bg-purple-50'
                        : isDark ? 'border-white/20 hover:border-white/40' : 'border-slate-300 hover:border-slate-400'
                    }`}
                  >
                    <input
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      onChange={handleMediaUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    {mediaFiles.length === 0 ? (
                      <>
                        <Plus className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                        <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                          Click to upload or drag and drop
                        </p>
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                          PNG, JPG or MP4 up to 10MB. Max 10 files.
                        </p>
                      </>
                    ) : (
                      <div className="flex flex-wrap gap-2 justify-center">
                        {mediaPreviewUrls.map((url, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={url}
                              alt={`Preview ${index + 1}`}
                              className="w-20 h-20 object-cover rounded-lg"
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeMediaFile(index);
                              }}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <div className={`w-20 h-20 rounded-lg border-2 border-dashed flex items-center justify-center ${
                          isDark ? 'border-white/20' : 'border-slate-300'
                        }`}>
                          <Plus className={`w-6 h-6 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Add from media library button */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setShowMediaLibrary(true);
                      loadMediaLibrary();
                    }}
                    className={`w-full mt-3 py-3 px-4 rounded-xl border transition-colors flex items-center justify-center gap-2 ${
                      isDark
                        ? 'border-white/10 hover:bg-white/5 text-gray-300'
                        : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <RefreshCw className="w-4 h-4" />
                    Add from media library
                  </button>
                </div>

                {/* Instructions */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                    Instructions for AI (optional)
                  </label>
                  <textarea
                    value={mediaInstructions}
                    onChange={(e) => setMediaInstructions(e.target.value)}
                    placeholder="E.g., Write an engaging caption for LinkedIn about our new product launch..."
                    rows={4}
                    className={`w-full px-4 py-3 rounded-xl border resize-none ${
                      isDark
                        ? 'bg-dark-700 border-white/10 text-white placeholder:text-gray-500'
                        : 'bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400'
                    } focus:outline-none focus:ring-2 focus:ring-purple-500/50`}
                  />
                </div>

                {/* Generate Button */}
                <button
                  onClick={generatePostFromMedia}
                  disabled={(mediaFiles.length === 0 && mediaPreviewUrls.length === 0) || isGeneratingFromMedia}
                  className={`w-full py-3 px-4 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
                    (mediaFiles.length === 0 && mediaPreviewUrls.length === 0)
                      ? isDark ? 'bg-white/10 text-gray-500 cursor-not-allowed' : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : 'bg-purple-500 hover:bg-purple-600 text-white'
                  }`}
                >
                  {isGeneratingFromMedia ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating post...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate post
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Generated Post Sidebar */}
      <AnimatePresence>
        {showGeneratedPostSidebar && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-40"
              onClick={() => setShowGeneratedPostSidebar(false)}
            />
            {/* Sidebar */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className={`fixed top-0 right-0 h-full w-full max-w-md z-50 ${
                isDark ? 'bg-dark-800' : 'bg-white'
              } shadow-2xl flex flex-col`}
            >
              {/* Sidebar Header */}
              <div className={`flex items-center justify-between p-4 border-b ${
                isDark ? 'border-white/10' : 'border-slate-200'
              }`}>
                <button
                  onClick={() => setShowGeneratedPostSidebar(false)}
                  className={`p-1 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                >
                  <X className={`w-5 h-5 ${isDark ? 'text-white' : 'text-slate-700'}`} />
                </button>
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Generated Post
                </h2>
                <div className="w-7" />
              </div>

              {/* Sidebar Content */}
              <div className="flex-1 overflow-y-auto">
                {/* Image Preview */}
                {generatedPostImage && (
                  <div className="p-4">
                    <img
                      src={generatedPostImage}
                      alt="Post preview"
                      className="w-full aspect-square object-cover rounded-xl"
                    />
                  </div>
                )}

                {/* Content Section */}
                <div className="px-4 pb-4">
                  <p className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Content</p>
                  <textarea
                    value={generatedPostContent}
                    onChange={(e) => setGeneratedPostContent(e.target.value)}
                    rows={6}
                    className={`w-full p-4 rounded-xl resize-none ${
                      isDark ? 'bg-dark-700 text-white' : 'bg-slate-100 text-slate-900'
                    } focus:outline-none focus:ring-2 focus:ring-purple-500/50`}
                  />
                </div>

                {/* Publish Date */}
                <div className="px-4 pb-4">
                  <p className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Publish date</p>
                  <div className={`flex items-center justify-between p-3 rounded-xl border ${
                    isDark ? 'border-white/10 bg-dark-700' : 'border-slate-200 bg-slate-50'
                  }`}>
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={generatedPostDate}
                        onChange={(e) => setGeneratedPostDate(e.target.value)}
                        className={`bg-transparent border-none outline-none ${isDark ? 'text-white' : 'text-slate-900'}`}
                      />
                      <span className={isDark ? 'text-gray-400' : 'text-slate-500'}>at</span>
                      <input
                        type="time"
                        value={generatedPostTime}
                        onChange={(e) => setGeneratedPostTime(e.target.value)}
                        className={`bg-transparent border-none outline-none ${isDark ? 'text-white' : 'text-slate-900'}`}
                      />
                    </div>
                    <Calendar className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-400'}`} />
                  </div>
                </div>

                {/* Platform Selection */}
                <div className="px-4 pb-4">
                  <p className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Platform</p>
                  <div className="flex gap-2 flex-wrap">
                    {['linkedin', 'facebook', 'instagram', 'twitter'].map((platform) => (
                      <button
                        key={platform}
                        onClick={() => setGeneratedPostPlatform(platform)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-colors ${
                          generatedPostPlatform === platform
                            ? 'bg-purple-500 border-purple-500 text-white'
                            : isDark
                              ? 'border-white/10 text-gray-300 hover:bg-white/5'
                              : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded flex items-center justify-center ${
                          generatedPostPlatform === platform ? 'text-white' : getProviderColor(platform)
                        }`}>
                          {getProviderIcon(platform)}
                        </div>
                        <span className="capitalize text-sm">{platform}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer with Schedule Button */}
              <div className={`p-4 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                <button
                  onClick={scheduleGeneratedPost}
                  disabled={isSchedulingPost || !generatedPostContent}
                  className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSchedulingPost ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Scheduling...
                    </>
                  ) : (
                    <>
                      <Calendar className="w-4 h-4" />
                      Schedule post
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Media Library Modal */}
      <AnimatePresence>
        {showMediaLibrary && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
            onClick={() => setShowMediaLibrary(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`w-full max-w-2xl max-h-[80vh] rounded-2xl ${isDark ? 'bg-dark-800' : 'bg-white'} shadow-2xl overflow-hidden flex flex-col`}
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={`flex items-center justify-between p-4 border-b ${
                isDark ? 'border-white/10' : 'border-slate-200'
              }`}>
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Media Library
                </h2>
                <button
                  onClick={() => setShowMediaLibrary(false)}
                  className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                >
                  <X className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {isLoadingMediaLibrary ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className={`w-8 h-8 animate-spin ${isDark ? 'text-purple-400' : 'text-purple-500'}`} />
                  </div>
                ) : mediaLibraryItems.length === 0 ? (
                  <div className="text-center py-12">
                    <ImageIcon className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-slate-300'}`} />
                    <p className={`text-lg font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                      No media found
                    </p>
                    <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                      Upload images to your Brain AI or add a brand logo to see them here.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Brand Logos Section */}
                    {mediaLibraryItems.filter(item => item.type === 'logo').length > 0 && (
                      <div>
                        <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                          Brand Logo
                        </h3>
                        <div className="grid grid-cols-4 gap-3">
                          {mediaLibraryItems.filter(item => item.type === 'logo').map((item) => (
                            <button
                              key={item.id}
                              onClick={() => handleSelectLibraryImage(item.url)}
                              className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all hover:scale-105 ${
                                selectedLibraryImage === item.url
                                  ? 'border-purple-500 ring-2 ring-purple-500/30'
                                  : isDark ? 'border-white/10 hover:border-white/30' : 'border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              <img
                                src={item.url}
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Website Images Section */}
                    {mediaLibraryItems.filter(item => item.type === 'website').length > 0 && (
                      <div>
                        <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                          Website Images
                        </h3>
                        <div className="grid grid-cols-4 gap-3">
                          {mediaLibraryItems.filter(item => item.type === 'website').map((item) => (
                            <button
                              key={item.id}
                              onClick={() => handleSelectLibraryImage(item.url)}
                              className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all hover:scale-105 ${
                                selectedLibraryImage === item.url
                                  ? 'border-purple-500 ring-2 ring-purple-500/30'
                                  : isDark ? 'border-white/10 hover:border-white/30' : 'border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              <img
                                src={item.url}
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Brain AI Images Section */}
                    {mediaLibraryItems.filter(item => item.type === 'brain').length > 0 && (
                      <div>
                        <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                          Brain AI Documents
                        </h3>
                        <div className="grid grid-cols-4 gap-3">
                          {mediaLibraryItems.filter(item => item.type === 'brain').map((item) => (
                            <button
                              key={item.id}
                              onClick={() => handleSelectLibraryImage(item.url)}
                              className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all hover:scale-105 ${
                                selectedLibraryImage === item.url
                                  ? 'border-purple-500 ring-2 ring-purple-500/30'
                                  : isDark ? 'border-white/10 hover:border-white/30' : 'border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              <img
                                src={item.url}
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Generated Images Section */}
                    {mediaLibraryItems.filter(item => item.type === 'generated').length > 0 && (
                      <div>
                        <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                          Generated Images
                        </h3>
                        <div className="grid grid-cols-4 gap-3">
                          {mediaLibraryItems.filter(item => item.type === 'generated').map((item) => (
                            <button
                              key={item.id}
                              onClick={() => handleSelectLibraryImage(item.url)}
                              className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all hover:scale-105 ${
                                selectedLibraryImage === item.url
                                  ? 'border-purple-500 ring-2 ring-purple-500/30'
                                  : isDark ? 'border-white/10 hover:border-white/30' : 'border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              <img
                                src={item.url}
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Brand Kit Modal */}
      <BrandKitModal
        isOpen={showBrandKitModal}
        onClose={() => setShowBrandKitModal(false)}
      />

    </div>
  );
}
